import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Mail, Loader2, CheckCircle2, XCircle, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";

export function EmailDiagnostics() {
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [lastResult, setLastResult] = useState<{
    type: 'success' | 'error' | 'warning';
    message: string;
    details?: string;
  } | null>(null);

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error("Please enter a test email address");
      return;
    }

    try {
      setTesting(true);
      setLastResult(null);

      console.log("📧 Testing email configuration with send-email function...");
      
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: [testEmail],
          subject: "Email Diagnostics Test",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h1 style="color: #2563eb;">Email System Test</h1>
              <p>This is a test email from your Lovable Cloud backend.</p>
              <p>If you're seeing this, your email configuration is working correctly!</p>
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #e5e7eb;">
              <p style="color: #6b7280; font-size: 12px;">
                Sent from Platform Admin Email Diagnostics
              </p>
            </div>
          `,
        }
      });

      if (error) {
        console.error("❌ Email test failed:", error);
        setLastResult({
          type: 'error',
          message: 'Email test failed',
          details: error.message
        });
        toast.error("Email test failed", { description: error.message });
        return;
      }

      if (data?.error) {
        console.error("❌ Email API error:", data.error);
        setLastResult({
          type: 'error',
          message: data.error,
          details: data.details || data.help
        });
        toast.error("Email configuration error", { description: data.error });
        return;
      }

      console.log("✅ Email test successful:", data);
      setLastResult({
        type: 'success',
        message: 'Test email sent successfully!',
        details: `Email ID: ${data?.id || 'N/A'}`
      });
      toast.success("Test email sent!", { 
        description: `Check ${testEmail} inbox (and spam folder)` 
      });
    } catch (error: any) {
      console.error("❌ Email test exception:", error);
      setLastResult({
        type: 'error',
        message: 'Failed to test email',
        details: error.message
      });
      toast.error("Failed to test email");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <CardTitle>Email Diagnostics</CardTitle>
        </div>
        <CardDescription>
          Test your email configuration and troubleshoot delivery issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Configuration Status */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Configuration Status</Label>
          <div className="flex flex-col gap-2 p-3 rounded-lg bg-muted/50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm">RESEND_API_KEY</span>
              <Badge variant="outline" className="gap-1">
                <AlertCircle className="w-3 h-3" />
                Check via test
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              The API key is stored securely in backend secrets. Test email sending to verify it's configured correctly.
            </p>
          </div>
        </div>

        {/* Test Email Section */}
        <div className="space-y-2">
          <Label htmlFor="test-email">Test Email Address</Label>
          <div className="flex gap-2">
            <Input
              id="test-email"
              type="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={testing}
            />
            <Button 
              onClick={handleTestEmail} 
              disabled={testing || !testEmail}
              className="gap-2 min-w-[120px]"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Test Email
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Send a test email to verify your configuration
          </p>
        </div>

        {/* Test Result */}
        {lastResult && (
          <Alert variant={lastResult.type === 'error' ? 'destructive' : 'default'}>
            {lastResult.type === 'success' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            {lastResult.type === 'error' && <XCircle className="h-4 w-4" />}
            {lastResult.type === 'warning' && <AlertCircle className="h-4 w-4 text-orange-600" />}
            <AlertDescription>
              <p className="font-semibold">{lastResult.message}</p>
              {lastResult.details && (
                <p className="text-xs mt-1 opacity-90">{lastResult.details}</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Common Issues */}
        <div className="space-y-2 pt-2 border-t">
          <Label className="text-sm font-semibold">Common Issues</Label>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex gap-2">
              <span className="text-primary">•</span>
              <div>
                <strong>RESEND_API_KEY not configured:</strong> Add your Resend API key in backend secrets
              </div>
            </div>
            <div className="flex gap-2">
              <span className="text-primary">•</span>
              <div>
                <strong>Domain not verified:</strong> Verify your sending domain at{" "}
                <a 
                  href="https://resend.com/domains" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  resend.com/domains
                </a>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="text-primary">•</span>
              <div>
                <strong>Emails going to spam:</strong> Check spam/promotions folder, safelist sender address
              </div>
            </div>
            <div className="flex gap-2">
              <span className="text-primary">•</span>
              <div>
                <strong>Password reset emails:</strong> Sent via Supabase Auth (separate from Resend). Check spam folder and auth settings in backend.
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
